import styles from './MailboxSettingsSidebar.module.scss';

import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  Box,
  Checkbox,
  Collapse as MuiCollapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useEventCallback } from '@mui/material/utils';
import {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Collapse } from '@work4all/components/lib/components/collapse';
import {
  IStackItem,
  useHistoryStack,
} from '@work4all/components/lib/navigation/history-stack';
import { NavigationOverlay } from '@work4all/components/lib/navigation/navigation-overlay';

import { useMailboxFoldersOnDemand } from '../../../hooks/use-mailboxes-data';
import { settings, useSetting } from '../../../settings';

import { BlockedSenders } from './BlockedSenders';
import { listStyleOverrides } from './list-style-overrides';
import {
  MailboxFolderTreeItem,
  makeFolderTree,
  ROOT_PARENT_ID,
} from './make-folder-tree';
import { ExtendedMailbox } from './types';

export interface MailboxSettingsSidebarProps {
  mailbox: ExtendedMailbox;
  open: boolean;
  onClose: () => void;
  onDelete: () => void;
  onAfterBlockedSendersChange: () => void;
}

export function MailboxSettingsSidebar(props: MailboxSettingsSidebarProps) {
  const { open, onClose, mailbox } = props;

  const handleAfterBlockedSendersChange = useEventCallback(
    props.onAfterBlockedSendersChange
  );

  const handleDelete = useEventCallback(props.onDelete);

  const context = useMemo(() => {
    return {
      mailbox,
      onAfterBlockedSendersChange: handleAfterBlockedSendersChange,
      onDelete: handleDelete,
    };
  }, [mailbox, handleAfterBlockedSendersChange, handleDelete]);

  return (
    <Context.Provider value={context}>
      <NavigationOverlay
        classes={{ wrapper: styles.wrapper }}
        open={open}
        close={onClose}
        initialView={VIEW}
        withBreadcrumbs={false}
      />
    </Context.Provider>
  );
}

const VIEW: IStackItem = { view: <Content /> };

const Context = createContext<{
  mailbox: ExtendedMailbox;
  onAfterBlockedSendersChange: () => void;
  onDelete: () => void;
}>(null);

function Content() {
  const props = useContext(Context);

  const { t } = useTranslation();

  const { mailbox, onAfterBlockedSendersChange, onDelete } = props;

  // Hook for loading folders on demand
  const { loadFolderChildren, getFolderChildren, isLoading } =
    useMailboxFoldersOnDemand(mailbox?.id || null);

  const hideChildrenRecursively = (
    folderId: string,
    visibilityState: Record<string, boolean>
  ) => {
    const children = getFolderChildren(folderId);

    for (const child of children) {
      visibilityState[child.id] = false;
      hideChildrenRecursively(child.id, visibilityState);
    }
  };

  // Fetch actual children information for all folders when sidebar opens
  useEffect(() => {
    if (mailbox?.folder) {
      mailbox.folder.forEach((folder) => {
        if (folder.childFolderCount > 0) {
          loadFolderChildren(folder.id);
        }
      });
    }
  }, [mailbox?.folder, loadFolderChildren]);

  const rootFolders = useMemo(() => {
    return makeFolderTree(mailbox?.folder ?? []);
  }, [mailbox]);

  const stack = useHistoryStack();

  const [collapsedFolders, setCollapsedFolders] = useState<
    Record<string, boolean>
  >(() => {
    // Initialize all folders as collapsed by default
    const initialCollapsed: Record<string, boolean> = {};
    if (mailbox?.folder) {
      mailbox.folder.forEach((folder) => {
        initialCollapsed[folder.id] = true; // true means collapsed
      });
    }
    return initialCollapsed;
  });

  // Initialize collapsed state for newly loaded children
  useEffect(() => {
    if (mailbox?.folder) {
      const newCollapsedState = { ...collapsedFolders };
      let hasChanges = false;

      mailbox.folder.forEach((folder) => {
        const loadedChildren = getFolderChildren(folder.id);
        loadedChildren.forEach((child) => {
          if (!(child.id in newCollapsedState)) {
            newCollapsedState[child.id] = true; // true means collapsed
            hasChanges = true;
          }
        });
      });

      if (hasChanges) {
        setCollapsedFolders(newCollapsedState);
      }
    }
  }, [mailbox?.folder, getFolderChildren, collapsedFolders]);

  const setting = useSetting(settings.mailboxFolderVisibility());

  const visibility = setting.value[mailbox.id];

  const setVisibility = (visibility: Record<string, boolean>) => {
    setting.set({ ...setting.value, [mailbox.id]: visibility });
  };

  const onFolderVisibilityChange = (folder: MailboxFolderTreeItem) => {
    const isVisible = folder.folder.isVisible;

    const newVisibility = {
      ...visibility,
      [folder.folder.id]: !isVisible,
    };

    if (!isVisible) {
      // Parent is being selected (shown), clear children visibility state
      const subfolders = collectSubfolders(folder);
      for (const subfolder of subfolders) {
        delete newVisibility[subfolder.folder.id];
      }
    } else {
      // Parent is being unselected (hidden), also hide all children
      hideChildrenRecursively(folder.folder.id, newVisibility);
    }

    setVisibility(newVisibility);
  };

  const renderFolders = (
    folders: MailboxFolderTreeItem[],
    parentId: string,
    forceHidden = false,
    _parentVisibility = true,
    depth = 0,
    maxDepth = 2
  ) => {
    const isOpen = !collapsedFolders[parentId];

    return (
      <MuiCollapse in={isOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ pl: '1rem' }}>
          {folders.map((folder) => {
            const folderId = folder.folder.id;

            const isVisible = folder.folder.isVisible && !forceHidden;

            const isOpen = !collapsedFolders[folderId];
            const loadedChildren = getFolderChildren(folderId);
            const isChildrenLoading = isLoading(folderId);
            // Show expand chevron only if we have actual children or are currently loading
            const hasChildren =
              folder.children != null ||
              loadedChildren.length > 0 ||
              isChildrenLoading;

            const onToggleOpen = () => {
              const newCollapsedState = !collapsedFolders[folder.folder.id];
              setCollapsedFolders((collapsed) => ({
                ...collapsed,
                [folder.folder.id]: newCollapsedState,
              }));

              // If expanding and folder might have children, load them
              if (
                !newCollapsedState &&
                (folder.children != null || folder.folder.childFolderCount > 0)
              ) {
                loadFolderChildren(folder.folder.id);
              }
            };

            const handleToggleVisibility = () => {
              onFolderVisibilityChange(folder);
            };

            return (
              <Fragment key={folderId}>
                <ListItem
                  secondaryAction={
                    hasChildren && !isChildrenLoading ? (
                      <IconButton size="small" onClick={onToggleOpen}>
                        <ExpandMore
                          sx={{
                            transition: 'transform 0.1s',
                            transform: isOpen ? null : 'rotate(-90deg)',
                          }}
                        />
                      </IconButton>
                    ) : null
                  }
                  disablePadding
                  sx={listStyleOverrides.listItem}
                >
                  <ListItemButton
                    disabled={forceHidden}
                    role={undefined}
                    onClick={handleToggleVisibility}
                    sx={listStyleOverrides.listItemButton}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        disabled={forceHidden}
                        checked={isVisible}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText primary={folder.folder.name} />
                  </ListItemButton>
                </ListItem>

                {isOpen && hasChildren && depth < maxDepth ? (
                  <MuiCollapse in={true} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ pl: '1rem' }}>
                      {isChildrenLoading ? (
                        <ListItem disablePadding>
                          <ListItemText primary="Loading..." />
                        </ListItem>
                      ) : (
                        loadedChildren.map((childFolder) => {
                          const childFolderId = childFolder.id;
                          const childIsVisible =
                            visibility[childFolderId] !== false &&
                            !forceHidden &&
                            isVisible;

                          const childFolderItem: MailboxFolderTreeItem = {
                            folder: {
                              id: childFolder.id,
                              name: childFolder.name,
                              childFolderCount: childFolder.childFolderCount,
                              isVisible: childIsVisible,
                            },
                            children: null,
                          };

                          return renderFolderItem(
                            childFolderItem,
                            childFolderId,
                            forceHidden || !isVisible,
                            isVisible,
                            depth + 1,
                            maxDepth
                          );
                        })
                      )}
                    </List>
                  </MuiCollapse>
                ) : null}
              </Fragment>
            );
          })}
        </List>
      </MuiCollapse>
    );
  };

  const renderFolderItem = (
    folder: MailboxFolderTreeItem,
    folderId: string,
    forceHidden = false,
    parentVisible = true,
    depth = 0,
    maxDepth = 2
  ) => {
    const isVisible =
      visibility[folderId] !== false && !forceHidden && parentVisible;
    const isOpen = !collapsedFolders[folderId];
    const loadedChildren = getFolderChildren(folderId);
    const isChildrenLoading = isLoading(folderId);
    const hasChildren =
      folder.children != null ||
      loadedChildren.length > 0 ||
      isChildrenLoading ||
      (folder.folder.childFolderCount && folder.folder.childFolderCount > 0);

    const onToggleOpen = () => {
      const newCollapsedState = !collapsedFolders[folderId];
      setCollapsedFolders((collapsed) => ({
        ...collapsed,
        [folderId]: newCollapsedState,
      }));

      // If expanding and folder has children, load them
      if (!newCollapsedState && hasChildren) {
        loadFolderChildren(folderId);
      }
    };

    const handleToggleVisibility = () => {
      const folderIsVisible = visibility[folderId] !== false;
      const newVisibility = {
        ...visibility,
        [folderId]: !folderIsVisible,
      };

      // If folder is being unselected (hidden), recursively hide all its children
      if (folderIsVisible) {
        hideChildrenRecursively(folderId, newVisibility);
      }

      setVisibility(newVisibility);
    };

    return (
      <Fragment key={folderId}>
        <ListItem
          secondaryAction={
            hasChildren ? (
              <IconButton size="small" onClick={onToggleOpen}>
                <ExpandMore
                  sx={{
                    transition: 'transform 0.1s',
                    transform: isOpen ? null : 'rotate(-90deg)',
                  }}
                />
              </IconButton>
            ) : null
          }
          disablePadding
          sx={listStyleOverrides.listItem}
        >
          <ListItemButton
            disabled={forceHidden || !parentVisible}
            role={undefined}
            onClick={handleToggleVisibility}
            sx={listStyleOverrides.listItemButton}
          >
            <ListItemIcon>
              <Checkbox
                edge="start"
                disabled={forceHidden || !parentVisible}
                checked={isVisible}
                tabIndex={-1}
                disableRipple
              />
            </ListItemIcon>
            <ListItemText primary={folder.folder.name} />
          </ListItemButton>
        </ListItem>

        {isOpen && hasChildren && depth < maxDepth ? (
          <MuiCollapse in={true} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ pl: '1rem' }}>
              {isChildrenLoading ? (
                <ListItem disablePadding>
                  <ListItemText primary="Loading..." />
                </ListItem>
              ) : (
                loadedChildren.map((childFolder) => {
                  const childFolderId = childFolder.id;

                  const childFolderItem: MailboxFolderTreeItem = {
                    folder: {
                      id: childFolder.id,
                      name: childFolder.name,
                      childFolderCount: childFolder.childFolderCount,
                      isVisible: visibility[childFolderId] !== false,
                    },
                    children: null,
                  };

                  return renderFolderItem(
                    childFolderItem,
                    childFolderId,
                    forceHidden || !isVisible,
                    isVisible,
                    depth + 1,
                    maxDepth
                  );
                })
              )}
            </List>
          </MuiCollapse>
        ) : null}
      </Fragment>
    );
  };

  return (
    <Stack direction="column">
      <div className={styles.toolbar}>
        <div className={styles['toolbar-left']}>
          <IconButton onClick={() => stack.close()}>
            <CloseIcon />
          </IconButton>
          <Typography variant="h3">{t('COMMON.MAILBOX')}</Typography>
          <Typography variant="h3"> | </Typography>
          <Typography variant="h3" color="var(--text03)">
            {mailbox.mailboxPrimaryAddress}
          </Typography>
        </div>
        <IconButton onClick={onDelete}>
          <DeleteIcon color="primary" />
        </IconButton>
      </div>

      <Box sx={{ flex: 'none', overflow: 'auto' }}>
        <Collapse
          title={t('ASSIGN_INCOMING_EMAILS.SHOW_FOLDERS')}
          defaultOpen={true}
        >
          {renderFolders(rootFolders, ROOT_PARENT_ID)}
        </Collapse>
      </Box>

      <Box sx={{ flex: 'none', overflow: 'auto' }}>
        <Collapse
          title={t('ASSIGN_INCOMING_EMAILS.HIDE_ADDRESSES')}
          defaultOpen={true}
        >
          <BlockedSenders
            onAfterBlockedSendersChange={onAfterBlockedSendersChange}
          />
        </Collapse>
      </Box>
    </Stack>
  );
}

function collectSubfolders(
  folder: MailboxFolderTreeItem
): MailboxFolderTreeItem[] {
  const subfolders: MailboxFolderTreeItem[] = [];

  collectSubfoldersRecursive(folder);

  return subfolders;

  function collectSubfoldersRecursive(folder: MailboxFolderTreeItem): void {
    if (folder.children) {
      for (const child of folder.children) {
        subfolders.push(child);
        collectSubfoldersRecursive(child);
      }
    }
  }
}
